import { Inject, Injectable, Optional, SkipSelf } from "@angular/core";

import { HttpClient } from "@angular/common/http";
import { Observable, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { BasicDataSubService } from '../../../../shared/basic-data-management/basic-data-subservice.class';
import { CachedDataSurveyService } from '../../../../technical-modules/tasks/services/cached-data-survey.service';

import { GroupementParc } from '../entities/groupement-parc.entity';

@Injectable({ providedIn: 'root' })
export class GroupementParcService {
	
	private path: string;

	private gpParcSubService!: BasicDataSubService<GroupementParc>;
	
	private notifyChanges = () => {
		// Indique immediatement un changement sur les donnees aux caches sans que le systeme de surveillance ait a attendre sa prochaine verification 
		this.cachedDataSurveyService.notifyImmediateChange("Groupement");
		this.cachedDataSurveyService.notifyImmediateChange("GroupementParc");
	};

	constructor( //
		@Inject(HttpClient) private httpClient: HttpClient, //
		@Inject(CachedDataSurveyService) private cachedDataSurveyService: CachedDataSurveyService, //
		@Optional() @SkipSelf() previousInstance?: GroupementParcService //
	) {
		if (previousInstance) {
			throw new Error(
				'GroupementParcService is already loaded. Import it in the AppModule only');
		}
		this.path = (window as any).baseApiPath;
		
		// Combiner les notifications de changement de Groupement et GroupementParc car un groupement de parc dans la partie cliente est la combinaison de ces deux entites Java
		let groupementChanged$ = this.cachedDataSurveyService.getChangeNotification("Groupement");
		let groupementParcChanged$ = this.cachedDataSurveyService.getChangeNotification("GroupementParc");
		let dataChanged$ = combineLatest([groupementChanged$, groupementParcChanged$]).pipe(
			map(
				timestamps => timestamps.reduce((date1, date2) => date1.getTime() < date2.getTime() ? date2 : date1)
			) 
		)
		this.gpParcSubService = new BasicDataSubService<GroupementParc>(httpClient, this.path + 'groupementParcs', dataChanged$);
	}

	// methode permettant le chargement de tous les groupement de parcs
	public getAll(): Observable<GroupementParc[]> {
		return this.gpParcSubService.getList();
	}

	// methode permettant le chargement d'un groupement de parc par son id
	public getById(id: number): Observable<GroupementParc> {
		return this.httpClient.get<GroupementParc>(this.path + 'groupementParcs/' + id);
	}

	// methode retournant les éléments frères d'un parc au sein d'un groupement
	public findParcSiblings(parcId: number): Observable<number[]> {
		return this.httpClient.get<number[]>(this.path + 'groupementParcs/siblings/' + parcId);
	}

	// methode retournant le groupement de ce parc s'il en a 
	public findbyParcId(parcId: number): Observable<GroupementParc> {
		return this.httpClient.get<GroupementParc>(this.path + 'groupementParcs/parc/' + parcId);
	}
	
	// methode permettant la sauvegarde d'un nouveau GroupementParc
	public create(gp: GroupementParc): Observable<GroupementParc> {
		const url = this.path + 'groupementParcs';
		const request$ = this.httpClient.post<GroupementParc>(url, gp, {}).pipe(
			tap(this.notifyChanges)
		);
		return request$;
	}
	
	// methode permettant la sauvegarde d'une nouvelle version d'un GroupementParc deja existant
	public update(gp: GroupementParc): Observable<GroupementParc> {
		const url = this.path + 'groupementParcs';
		const request$ = this.httpClient.put<GroupementParc>(url, gp, {}).pipe(
			tap(this.notifyChanges)
		);
		return request$;
	}
	
	// methode permettant la suppression d'un GroupementParc
	public delete(gpId: number): Observable<any> {
		const url = this.path + 'groupementParcs/' + gpId;
		const request$ = this.httpClient.delete<any>(url, {}).pipe(
			tap(this.notifyChanges)
		);
		return request$;
	}
	
	public export(): Observable<Blob> {
		return this.httpClient.get(this.path + 'groupementParcs/export', {responseType: 'blob'});
	}
	
}